import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SPACE_TYPES, SPACE_COLORS, SPACE_ICONS } from "@/lib/constants";
import { ENVIRONMENTS, ENV_ORDER, TYPE_TO_ENV, tint } from "@/lib/environments";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export const CreateSpaceDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [type, setType] = useState("familiar");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("🏡");
  const [color, setColor] = useState(SPACE_COLORS[0]);
  const [environment, setEnvironment] = useState("family");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setStep(1); setType("familiar"); setName(""); setDescription(""); setIcon("🏡"); setColor(SPACE_COLORS[0]); setEnvironment("family");
  };

  const handleCreate = async () => {
    if (!name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      const { data } = await api.post("/workspaces", { name, description, icon, color, type, environment });
      await qc.invalidateQueries({ queryKey: ["home"] });
      toast.success(`Espacio "${data.name}" creado`);
      onOpenChange(false);
      reset();
      navigate(`/space/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="create-space-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Crear un Espacio</DialogTitle>
          <DialogDescription>
            {step === 1 ? "Elige el tipo de Espacio. Configuraremos los roles por ti." : "Dale identidad a tu Espacio."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SPACE_TYPES.map((t) => (
              <button
                key={t.value}
                data-testid={`space-type-${t.value}`}
                onClick={() => { setType(t.value); setIcon(t.icon); setEnvironment(TYPE_TO_ENV[t.value] || "other"); setStep(2); }}
                className={`text-left p-4 rounded-xl border transition-all hover:border-primary hover:bg-accent/50 ${
                  type === t.value ? "border-primary bg-accent/50" : "border-border"
                }`}
              >
                <span className="text-2xl">{t.icon}</span>
                <p className="font-semibold mt-2">{t.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="space-name">Nombre</Label>
              <Input id="space-name" data-testid="create-space-name-input" value={name}
                onChange={(e) => setName(e.target.value)} placeholder="Ej: Familia García" maxLength={100} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="space-desc">Descripción (opcional)</Label>
              <Textarea id="space-desc" data-testid="create-space-description-input" value={description}
                onChange={(e) => setDescription(e.target.value)} placeholder="¿Para qué es este Espacio?" rows={2} maxLength={500} />
            </div>
            <div className="space-y-2">
              <Label>Icono</Label>
              <div className="flex flex-wrap gap-1.5">
                {SPACE_ICONS.map((ic) => (
                  <button key={ic} data-testid={`space-icon-${ic}`} onClick={() => setIcon(ic)}
                    className={`h-9 w-9 rounded-lg text-lg flex items-center justify-center border transition-colors ${
                      icon === ic ? "border-primary bg-accent" : "border-transparent hover:bg-secondary"
                    }`}>
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {SPACE_COLORS.map((c) => (
                  <button key={c} data-testid={`space-color-${c.replace("#", "")}`} onClick={() => setColor(c)}
                    className={`h-8 w-8 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-primary scale-110" : ""}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Entorno</Label>
              <div className="flex flex-wrap gap-2">
                {ENV_ORDER.map((key) => {
                  const env = ENVIRONMENTS[key];
                  const active = environment === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      data-testid={`create-space-env-${key}`}
                      onClick={() => setEnvironment(key)}
                      className="px-3 h-8 rounded-full text-xs font-semibold flex items-center gap-1 transition-colors"
                      style={active
                        ? { backgroundColor: env.color, color: "#FFFFFF" }
                        : { backgroundColor: tint(env.color, 0.14), color: env.text }}
                    >
                      <span aria-hidden>{env.emoji}</span> {env.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" data-testid="create-space-back-btn" onClick={() => setStep(1)}>Atrás</Button>
              <Button data-testid="create-space-submit-btn" onClick={handleCreate} disabled={saving}>
                {saving ? "Creando..." : "Crear Espacio"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
